import React from 'react'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import { Grid, ListItemText, TextField } from '@mui/material'

import { SelectInput } from '../../../ui'
import { gridRowStyles } from './grid-row.styles'

/**
 * @typedef {object} GridRowItem
 * @property {string} label - ID de internacionalización para la etiqueta del campo.
 * @property {string} value - Nombre del campo en el objeto data para obtener el valor.
 * @property {'text' | 'input' | 'select' | 'combo'} [type] - Tipo de visualización del campo.
 * @property {string} [name] - Nombre del campo para el input (cuando type='input').
 * @property {string} [comboId] - ID del combo en el objeto combos (cuando type='combo' o type='select').
 * @property {function(object, IntlShape): any} [valueParsed] - Función personalizada para formatear el valor mostrado.
 */

/**
 * @typedef {object} GridRowComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {GridRowItem[]} items - Array de configuraciones de campos a mostrar en la fila.
 * @property {object} data - Objeto con los datos a mostrar.
 * @property {number} [spacing=4] - Espaciado entre elementos del Grid.
 * @property {'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'} [justify='flex-start'] - Justificación del contenido del Grid.
 * @property {object} [combos] - Objeto con arrays de opciones para campos tipo combo/select.
 * @property {function(Event): void} [onChange] - Callback ejecutado cuando cambia el valor de un input o select.
 * @property {boolean} [disabled] - Si es true, deshabilita todos los inputs y selects editables.
 */

/**
 * Un componente de fila de Grid que muestra campos de datos en formato label-value.
 *
 * Renderiza una fila horizontal de campos usando Material-UI Grid, donde cada campo puede ser:
 * - Texto estático (visualización simple)
 * - Input editable (con eventos onBlur y Enter)
 * - Select/Combo con opciones
 * - Valor formateado mediante función personalizada
 *
 * Los valores de combos se resuelven automáticamente usando lookups. Ideal para mostrar
 * detalles de registros en formato compacto y estructurado.
 *
 * @component
 * @example
 * const MyDetailView = () => {
 *   const data = {
 *     nombre: 'Juan Pérez',
 *     estado: 'activo',
 *     fecha: '2025-10-20',
 *     notas: 'Comentarios adicionales'
 *   };
 *
 *   const combos = {
 *     estados: {
 *       data: [
 *         { key: 'activo', value: 'Activo' },
 *         { key: 'inactivo', value: 'Inactivo' }
 *       ]
 *     }
 *   };
 *
 *   const items = [
 *     { label: 'detail.nombre', value: 'nombre', type: 'text' },
 *     { label: 'detail.estado', value: 'estado', type: 'combo', comboId: 'estados' },
 *     { label: 'detail.fecha', value: 'fecha', valueParsed: (data, intl) => intl.formatDate(data.fecha) },
 *     { label: 'detail.notas', value: 'notas', type: 'input', name: 'notas' }
 *   ];
 *
 *   const handleChange = (event) => {
 *     console.log('Campo cambiado:', event.target.name, event.target.value);
 *   };
 *
 *   return (
 *     <GridRowComponent
 *       items={items}
 *       data={data}
 *       combos={combos}
 *       onChange={handleChange}
 *       spacing={3}
 *       justify="flex-start"
 *     />
 *   );
 * };
 *
 * @param {GridRowComponentProps} props - Las props del componente GridRowComponent.
 */
export const GridRowComponent = withStyles(gridRowStyles)(({
  classes,
  items,
  data,
  spacing = 4,
  justify = 'flex-start',
  ...props
}) => {
  const intl = useIntl()

  const handleEnterKey = (event) => {
    if (event.keyCode === 13) {
      props.onChange(event)
    }
  }

  const getValueByType = (item, data) => {
    if (data[item.value] === null) return ''
    if (item.type === 'combo') {
      let selectedOption =
        props.combos &&
        props.combos[item.comboId].data &&
        props.combos[item.comboId].data.length !== 0 &&
        props.combos[item.comboId].data.find((o) => o.key === data[item.value])
      if (!selectedOption) {
        selectedOption = {}
        console.debug('WARNING: Value mismatch', props.combos, 'item ', item, 'data ', data)
      }
      return selectedOption.value || data[item.value]
    } else return data[item.value]
  }

  return (
    <Grid container spacing={spacing} justifyContent={justify}>
      {items.map((item, i) => (
        <Grid key={i}>
          <ListItemText
            primary={intl.formatMessage({ id: item.label })}
            secondary={
              <span style={{ whiteSpace: 'pre-line' }}>
                {item.type === 'input' ? (
                  <TextField
                    id="outlined-basic"
                    name={item.name}
                    variant="outlined"
                    defaultValue={
                      item.valueParsed ? item.valueParsed(data, intl) : data[item.value]
                    }
                    onBlur={props.onChange}
                    onKeyDown={handleEnterKey}
                    size="small"
                    disabled={props.disabled}
                  />
                ) : item.type === 'select' ? (
                  <SelectInput
                    name={item.value}
                    label={item.label}
                    value={data[item.value]}
                    onChange={props.onChange}
                    values={
                      props.combos && props.combos[item.comboId]
                        ? props.combos[item.comboId].data
                        : []
                    }
                    disabled={props.disabled}
                    // loading={!props.combos[item.value] || props.combos[item.value].isLoading}
                  />
                ) : item.valueParsed ? (
                  item.valueParsed(data, intl) || ''
                ) : (
                  getValueByType(item, data)
                )}
              </span>
            }
            primaryTypographyProps={{ variant: 'subtitle2' }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </Grid>
      ))}
    </Grid>
  )
})
